import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, PhotoIcon, CheckIcon, MagnifyingGlassIcon, StarIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { IProjectAsset, AssetType, CharacterGender, CharacterGenderLabels, Locale, SystemAssetQuery } from '@packages/global';
import { useAssets } from '../../hooks/useAssets';
import MediaPreviewModal from '../common/MediaPreviewModal';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { useToast } from '../../stores/toastStore';

interface UserAssetSelectorProps {
  isOpen: boolean;
  projectId?: string;
  episodeId?: string;
  onConfirm: (assets: IProjectAsset[]) => void;
  onClose: () => void;
}

interface AssetWithFavorite extends IProjectAsset {
  isFavorited?: boolean;
}

const assetTypeKeys = [
  { type: AssetType.CHARACTER, key: 'subject.character' },
  { type: AssetType.PROP, key: 'subject.prop' },
  { type: AssetType.SCENE, key: 'subject.scene' }
];

type AssetScope = 'user' | 'project' | 'episode' | 'system';

// 检查是否为认证角色
const isVerifiedCharacter = (asset: IProjectAsset): boolean => {
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

export default function UserAssetSelector({ isOpen, projectId, episodeId, onConfirm, onClose }: UserAssetSelectorProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType>(AssetType.CHARACTER);
  const [selectedAssets, setSelectedAssets] = useState<IProjectAsset[]>([]);
  const [activeScope, setActiveScope] = useState<AssetScope>('user');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { success, error } = useToast();

  // system scope 专用状态
  const [systemAssets, setSystemAssets] = useState<AssetWithFavorite[]>([]);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemSearch, setSystemSearch] = useState('');
  const [systemSearchInput, setSystemSearchInput] = useState('');
  const [systemPage, setSystemPage] = useState(1);
  const [systemTotalPages, setSystemTotalPages] = useState(1);
  const [systemTotal, setSystemTotal] = useState(0);
  const SYSTEM_PAGE_SIZE = 12;

  // 筛选条件
  const [filterGender, setFilterGender] = useState<CharacterGender | ''>('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterAgeRange, setFilterAgeRange] = useState('');

  // Determine available scopes based on props
  const availableScopes = useMemo(() => {
    const scopes: { id: AssetScope; label: string }[] = [];
    if (episodeId) {
      scopes.push({ id: 'episode', label: t('asset.selector.episodeAssets') });
    }
    if (projectId) {
      scopes.push({ id: 'project', label: t('asset.selector.projectAssets') });
    }
    scopes.push({ id: 'user', label: t('asset.selector.myAssets') });
    scopes.push({ id: 'system', label: t('asset.selector.systemAssets') });
    return scopes;
  }, [projectId, episodeId, t]);

  // Reset active scope when opening or props change
  useEffect(() => {
    if (isOpen) {
      if (episodeId) setActiveScope('episode');
      else if (projectId) setActiveScope('project');
      else setActiveScope('user');
    }
  }, [isOpen, projectId, episodeId]);

  // Construct params for useAssets based on activeScope
  const useAssetsParams = useMemo(() => {
    switch (activeScope) {
      case 'episode': return { episodeId };
      case 'project': return { projectId };
      case 'user': return undefined;
      case 'system': return undefined;
      default: return undefined;
    }
  }, [activeScope, episodeId, projectId]);

  const { getAssetsByType, loading } = useAssets(useAssetsParams);

  // 加载系统主体库数据
  useEffect(() => {
    if (activeScope !== 'system' || !isOpen) return;

    const loadSystemAssets = async () => {
      setSystemLoading(true);
      try {
        const query: SystemAssetQuery = {
          assetType: selectedAssetType,
        };

        if (systemSearch) query.search = systemSearch;
        if (selectedAssetType === AssetType.CHARACTER) {
          if (filterGender) query.gender = filterGender;
          if (filterCountry) query.country = filterCountry;
          if (filterAgeRange) {
            const [min, max] = filterAgeRange.split('-').map(Number);
            query.ageMin = min;
            query.ageMax = max;
          }
        }

        const res = await projectAssetApi.systemAssetPageList({
          query,
          pagination: {
            page: systemPage,
            limit: SYSTEM_PAGE_SIZE
          }
        });
        setSystemAssets(res.items);
        setSystemTotal(res.pagination.total);
        setSystemTotalPages(res.pagination.totalPages);
      } catch (err) {
        error(t('asset.selector.loadSystemAssetsFailed'));
      } finally {
        setSystemLoading(false);
      }
    };

    loadSystemAssets();
  }, [activeScope, isOpen, selectedAssetType, systemPage, systemSearch, filterGender, filterCountry, filterAgeRange, error, t, SYSTEM_PAGE_SIZE]);

  // 重置系统主体库状态
  useEffect(() => {
    if (activeScope === 'system') {
      setSystemPage(1);
      setSystemSearch('');
      setSystemSearchInput('');
      setFilterGender('');
      setFilterCountry('');
      setFilterAgeRange('');
    }
  }, [activeScope, selectedAssetType]);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setSystemSearch(systemSearchInput);
      setSystemPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [systemSearchInput]);

  if (!isOpen) return null;

  const currentAssets = activeScope === 'system' ? systemAssets : getAssetsByType(selectedAssetType);
  const isLoading = activeScope === 'system' ? systemLoading : loading;

  const handleAssetToggle = (asset: IProjectAsset) => {
    setSelectedAssets(prev => {
      const exists = prev.find(a => a.id === asset.id);
      if (exists) {
        return prev.filter(a => a.id !== asset.id);
      } else {
        return [...prev, asset];
      }
    });
  };

  const handleFavoriteAsset = async (asset: AssetWithFavorite, e: React.MouseEvent) => {
    e.stopPropagation();
    if (asset.isFavorited) {
      success(t('projectSettings:subject.alreadyFavorited'));
      return;
    }

    try {
      await projectAssetApi.favoriteAsset(asset.id);
      success(t('projectSettings:subject.favoriteSuccess'));
      setSystemAssets(prev => prev.map(a =>
        a.id === asset.id ? { ...a, isFavorited: true } : a
      ));
    } catch (err) {
      error(t('projectSettings:subject.favoriteFailed'));
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedAssets);
    setSelectedAssets([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedAssets([]);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-bg-surface rounded-xl shadow-xl w-[800px] h-[600px] max-w-[800px] max-h-[600px] flex flex-col overflow-hidden border border-border-subtle">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            {availableScopes.length > 1 ? (
              <div className="flex gap-1 bg-bg-elevated p-1 rounded-lg">
                {availableScopes.map(scope => (
                  <button
                    key={scope.id}
                    onClick={() => setActiveScope(scope.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer ${activeScope === scope.id
                      ? 'bg-bg-surface text-gray-50 shadow-sm'
                      : 'text-gray-400 hover:text-gray-300 hover:bg-bg-surface/50'
                      }`}
                  >
                    {scope.label}
                  </button>
                ))}
              </div>
            ) : (
              <h2 className="text-lg font-semibold text-gray-50">
                {availableScopes[0]?.label || t('asset.selector.selectAsset')}
              </h2>
            )}
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-300 transition-colors cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border-subtle flex bg-bg-elevated/30">
            <div className="flex px-4">
              {assetTypeKeys.map((option) => (
                <button
                  key={option.type}
                  onClick={() => setSelectedAssetType(option.type)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer ${selectedAssetType === option.type
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                >
                  {t(option.key)}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* 系统主体库搜索和筛选 */}
            {activeScope === 'system' && (
              <div className="mb-4">
                {selectedAssetType === AssetType.CHARACTER ? (
                  /* 角色类型：筛选条件 + 搜索框在一行 */
                  <div className="flex gap-2">
                    <select
                      value={filterGender}
                      onChange={(e) => setFilterGender(e.target.value as CharacterGender | '')}
                      className="w-32 px-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg text-sm text-gray-300 focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    >
                      <option value="">{t('asset.selector.allGenders')}</option>
                      {Object.values(CharacterGender).map(gender => (
                        <option key={gender} value={gender}>
                          {CharacterGenderLabels[gender][i18n.language as Locale]}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      value={filterCountry}
                      onChange={(e) => setFilterCountry(e.target.value)}
                      placeholder={t('asset.selector.countryPlaceholder')}
                      className="w-32 px-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                    />

                    <select
                      value={filterAgeRange}
                      onChange={(e) => setFilterAgeRange(e.target.value)}
                      className="w-40 px-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg text-sm text-gray-300 focus:outline-none focus:border-primary transition-colors cursor-pointer"
                    >
                      <option value="">{t('asset.selector.allAges')}</option>
                      <option value="0-12">{t('asset.selector.ageRange.child')}</option>
                      <option value="13-17">{t('asset.selector.ageRange.teen')}</option>
                      <option value="18-35">{t('asset.selector.ageRange.youngAdult')}</option>
                      <option value="36-60">{t('asset.selector.ageRange.middleAged')}</option>
                      <option value="61-100">{t('asset.selector.ageRange.senior')}</option>
                    </select>

                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={systemSearchInput}
                        onChange={(e) => setSystemSearchInput(e.target.value)}
                        placeholder={t('asset.selector.searchPlaceholder')}
                        className="w-full pl-9 pr-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                      />
                    </div>
                  </div>
                ) : (
                  /* 道具/场景类型：只显示搜索框 */
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={systemSearchInput}
                      onChange={(e) => setSystemSearchInput(e.target.value)}
                      placeholder={t('asset.selector.searchPlaceholder')}
                      className="w-full pl-9 pr-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg text-sm text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-8 text-gray-400">{t('common:loading')}</div>
            ) : currentAssets.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                {activeScope === 'system' && systemSearch
                  ? t('asset.selector.noSearchResults')
                  : t('subject.emptyType', { type: t(assetTypeKeys.find(opt => opt.type === selectedAssetType)?.key || '') })}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {currentAssets.map((asset) => {
                  const isSelected = selectedAssets.some(a => a.id === asset.id);
                  const assetWithFavorite = asset as AssetWithFavorite;
                  const isVerified = isVerifiedCharacter(asset);
                  return (
                    <button
                      key={asset.id}
                      onClick={() => handleAssetToggle(asset)}
                      className={`p-3 text-left rounded-lg transition-colors border cursor-pointer relative group ${isSelected
                        ? 'bg-primary/10 border-primary'
                        : 'bg-bg-elevated hover:bg-bg-surface border-border-subtle'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          {asset.conceptImageUrl ? (
                            <img
                              src={asset.conceptImageUrl}
                              alt={asset.name}
                              className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage(asset.conceptImageUrl!);
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 bg-bg-surface rounded flex items-center justify-center border border-border-subtle">
                              <PhotoIcon className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          {/* 认证角标 */}
                          {isVerified && (
                            <div
                              className="absolute -top-1 -right-1 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-0.5 shadow-lg ring-1 ring-yellow-300/50"
                              title={t('asset.verified')}
                            >
                              <CheckBadgeIcon className="h-3 w-3 text-white drop-shadow-md" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm truncate ${isSelected ? 'text-primary' : 'text-gray-50'}`}>{asset.name}</div>
                          {asset.prompt && (
                            <div className="text-xs text-gray-400 truncate mt-1">{asset.prompt}</div>
                          )}
                        </div>
                        {isSelected && (
                          <CheckIcon className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </div>

                      {/* 系统主体库收藏按钮 */}
                      {activeScope === 'system' && (
                        <button
                          type="button"
                          onClick={(e) => handleFavoriteAsset(assetWithFavorite, e)}
                          className="absolute top-2 right-2 p-1 rounded bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-black/70"
                          title={assetWithFavorite.isFavorited ? t('projectSettings:subject.favorited') : t('projectSettings:subject.favoriteToLibrary')}
                        >
                          {assetWithFavorite.isFavorited
                            ? <StarIconSolid className="h-4 w-4 text-yellow-400" />
                            : <StarIcon className="h-4 w-4 text-yellow-400" />
                          }
                        </button>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border-subtle p-4 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {t('asset.selector.selectedCount', { count: selectedAssets.length })}
            </div>

            {/* 系统主体库分页 */}
            {activeScope === 'system' && systemTotalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSystemPage(p => Math.max(1, p - 1))}
                  disabled={systemPage === 1}
                  className="px-3 py-1.5 text-sm bg-bg-elevated border border-border-subtle rounded-lg text-gray-300 hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  {t('common:previous')}
                </button>
                <span className="text-sm text-gray-400">
                  {systemPage} / {systemTotalPages}
                </span>
                <button
                  onClick={() => setSystemPage(p => Math.min(systemTotalPages, p + 1))}
                  disabled={systemPage === systemTotalPages}
                  className="px-3 py-1.5 text-sm bg-bg-elevated border border-border-subtle rounded-lg text-gray-300 hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  {t('common:next')}
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-300 hover:text-gray-50 bg-bg-elevated hover:bg-bg-surface rounded-lg border border-border-subtle transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedAssets.length === 0}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {t('asset.selector.confirmSelect')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 图片预览弹窗 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('asset.assetImagePreview')}
      />
    </>
  );
}
